import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, X, CircleNotch } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '@/lib/api';

const inviteSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Minimum 8 caractères')
      .regex(/[A-Z]/, 'Au moins une majuscule')
      .regex(/[a-z]/, 'Au moins une minuscule')
      .regex(/[0-9]/, 'Au moins un chiffre')
      .regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  });

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteFormProps {
  token: string;
}

interface TokenInfo {
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
}

export function InviteForm({ token }: InviteFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
  });

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_URL}/invitation/${token}`);

        if (!response.ok) {
          const error = await response.json();
          toast.error('Lien invalide', {
            description: error.message || 'Ce lien d\'invitation n\'est pas valide ou a expiré',
            duration: 5000,
          });
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        const data = await response.json();
        setTokenInfo(data);
      } catch (error) {
        toast.error('Erreur', {
          description: 'Impossible de vérifier le lien d\'invitation',
          duration: 5000,
        });
        setTimeout(() => navigate('/login'), 2000);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, navigate]);

  const onSubmit = async (data: InviteFormValues) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/invitation/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: data.password }),
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error('Erreur', {
          description: error.message || 'Impossible de créer votre compte',
          duration: 5000,
        });
        return;
      }

      toast.success('Compte créé avec succès', {
        description: 'Vous pouvez maintenant vous connecter',
        duration: 5000,
      });

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error('Erreur', {
        description: 'Une erreur inattendue est survenue',
        duration: 5000,
      });
      console.error('Accept invitation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Règles de validation du mot de passe avec statut
  const passwordRules = [
    { label: 'Au moins 8 caractères', valid: password.length >= 8 },
    { label: 'Au moins une majuscule', valid: /[A-Z]/.test(password) },
    { label: 'Au moins une minuscule', valid: /[a-z]/.test(password) },
    { label: 'Au moins un chiffre', valid: /[0-9]/.test(password) },
    { label: 'Au moins un caractère spécial', valid: /[^A-Za-z0-9]/.test(password) },
  ];

  if (isVerifying) {
    return (
      <div className="flex flex-col items-center justify-center gap-4">
        <CircleNotch className="h-8 w-8 animate-spin text-brand-deep" />
        <p className="text-sm text-ink-muted">Vérification du lien d'invitation...</p>
      </div>
    );
  }

  if (!tokenInfo) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 rounded-goutte border border-rule bg-white p-6 sm:p-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-[30px] font-extrabold tracking-[-0.03em] text-ink">Bienvenue&nbsp;!</h1>
        <p className="text-sm text-ink-muted">
          Bonjour {tokenInfo.firstName} {tokenInfo.lastName}, vous avez été invité à rejoindre{' '}
          <span className="font-semibold">{tokenInfo.organizationName}</span>
        </p>
        <p className="text-sm text-ink-muted">
          Définissez votre mot de passe pour créer votre compte
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="password">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            {...register('password')}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            placeholder="••••••••"
          />
        </div>

        {/* Règles de validation en temps réel */}
        {password && (
          <div className="rounded-goutte-sm border border-rule bg-ground p-3">
            <p className="text-xs font-medium mb-2">Votre mot de passe doit contenir :</p>
            <div className="space-y-1">
              {passwordRules.map((rule, index) => (
                <div key={index} className="flex items-center gap-2 text-xs">
                  {rule.valid ? (
                    <Check className="h-3 w-3 text-green-600" />
                  ) : (
                    <X className="h-3 w-3 text-red-600" />
                  )}
                  <span className={rule.valid ? 'text-green-600' : 'text-ink-muted'}>
                    {rule.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-2">
          <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
          <Input
            id="confirmPassword"
            type="password"
            {...register('confirmPassword')}
            disabled={isLoading}
            placeholder="••••••••"
          />
          {errors.confirmPassword && (
            <p className="text-xs text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
              Création du compte...
            </>
          ) : (
            'Créer mon compte'
          )}
        </Button>
      </form>
    </div>
  );
}
